import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { FeatureFlagProvider } from '@/contexts/FeatureFlagContext';
import { HospitalProvider } from '@/contexts/HospitalContext';
import { BreadcrumbProvider } from '@/contexts/BreadcrumbContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AccessGate } from '@/components/AccessGate';
import { RoleGuard } from '@/components/RoleGuard';
import { ROLES } from '@/contexts/AuthContext';
import { AppLayout } from '@/layouts/AppLayout';
import { FEATURES } from '@/contexts/FeatureFlagContext';

import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import HospitalRegistrationPage from '@/pages/HospitalRegistrationPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import PatientsPage from '@/pages/PatientsPage';
import AppointmentsPage from '@/pages/AppointmentsPageEnhanced';
import AppointmentDetailPage from '@/pages/AppointmentDetailPage';
import IPDPage from '@/pages/IPDPage';
import PharmacyPage from '@/pages/PharmacyPage';
import LabPage from '@/pages/LabPage';
import BillingPage from '@/pages/BillingPage';
import InventoryPage from '@/pages/InventoryPage';
import HRPage from '@/pages/HRPage';
import DocumentsPage from '@/pages/DocumentsPage';
import AuditPage from '@/pages/AuditPage';
import SettingsPage from '@/pages/SettingsPage';
import UserManagementPage from '@/pages/UserManagementPage';
import RoleManagementPage from '@/pages/RoleManagementPage';
import DoctorCalendarPage from '@/pages/DoctorCalendarPage';

import '@/App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <FeatureFlagProvider>
            <HospitalProvider>
              <BreadcrumbProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/register-hospital" element={<HospitalRegistrationPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />

                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <AppLayout>
                          <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            
                            <Route
                              path="/dashboard"
                              element={
                                <AccessGate feature={FEATURES.DASHBOARD}>
                                  <DashboardPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/patients"
                              element={
                                <AccessGate feature={FEATURES.PATIENTS}>
                                  <PatientsPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/appointments"
                              element={
                                <AccessGate feature={FEATURES.APPOINTMENTS}>
                                  <AppointmentsPage />
                                </AccessGate>
                              }
                            />

                            <Route
                              path="/appointments/:id"
                              element={
                                <AccessGate feature={FEATURES.APPOINTMENTS}>
                                  <AppointmentDetailPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/ipd"
                              element={
                                <AccessGate feature={FEATURES.IPD}>
                                  <IPDPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/pharmacy"
                              element={
                                <AccessGate feature={FEATURES.PHARMACY}>
                                  <PharmacyPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/lab"
                              element={
                                <AccessGate feature={FEATURES.LAB}>
                                  <LabPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/billing"
                              element={
                                <AccessGate feature={FEATURES.BILLING}>
                                  <BillingPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/inventory"
                              element={
                                <AccessGate feature={FEATURES.INVENTORY}>
                                  <InventoryPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/hr"
                              element={
                                <AccessGate feature={FEATURES.HR}>
                                  <HRPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/documents"
                              element={
                                <AccessGate feature={FEATURES.DOCUMENTS}>
                                  <DocumentsPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/audit"
                              element={
                                <AccessGate feature={FEATURES.AUDIT}>
                                  <AuditPage />
                                </AccessGate>
                              }
                            />
                            
                            <Route
                              path="/users"
                              element={
                                <RoleGuard
                                  roles={[ROLES.SUPER_ADMIN, ROLES.HOSPITAL_ADMIN]}
                                  fallback={<Navigate to="/dashboard" replace />}
                                >
                                  <AccessGate feature={FEATURES.USERS}>
                                    <UserManagementPage />
                                  </AccessGate>
                                </RoleGuard>
                              }
                            />
                            <Route
                              path="/roles"
                              element={
                                <RoleGuard
                                  roles={[ROLES.SUPER_ADMIN, ROLES.HOSPITAL_ADMIN]}
                                  fallback={<Navigate to="/dashboard" replace />}
                                >
                                  <AccessGate feature={FEATURES.ROLE_MANAGEMENT}>
                                    <RoleManagementPage />
                                  </AccessGate>
                                </RoleGuard>
                              }
                            />
                            <Route
                              path="/my-calendar"
                              element={
                                <RoleGuard
                                  roles={[ROLES.DOCTOR]}
                                  fallback={<Navigate to="/dashboard" replace />}
                                >
                                  <AccessGate feature={FEATURES.MY_CALENDAR}>
                                    <DoctorCalendarPage />
                                  </AccessGate>
                                </RoleGuard>
                              }
                            />
                            <Route path="/settings" element={<SettingsPage />} />
                          </Routes>
                        </AppLayout>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </BrowserRouter>
              </BreadcrumbProvider>
              <Toaster position="top-right" richColors />
            </HospitalProvider>
          </FeatureFlagProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;